/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react'
import { Typography, Box, TextField, Button } from '@material-ui/core'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import {
  signIn,
  signUp,
  resetPassword,
  confirmResetPassword,
  confirmSignUp,
} from 'aws-amplify/auth'
import LogoGrid from '../../pages/LogoGrid'

const translateAuthError = (error: any): string => {
  const errorMessage = error.message || error.toString()
  const errorCode = error.name || ''

  // Map common Amplify errors to German
  const errorMap: { [key: string]: string } = {
    'User does not exist': 'Benutzer existiert nicht',
    'Incorrect username or password': 'Falsche E-Mail-Adresse oder Passwort',
    'User is not confirmed':
      'Benutzer ist nicht bestätigt. Bitte überprüfen Sie Ihre E-Mail.',
    'Invalid verification code provided': 'Ungültiger Bestätigungscode',
    'Code mismatch': 'Ungültiger Bestätigungscode',
    'Invalid code provided': 'Ungültiger Code',
    'User already exists': 'Benutzer existiert bereits',
    'Username/client id combination not found': 'E-Mail-Adresse nicht gefunden',
    'Attempt limit exceeded':
      'Zu viele Versuche. Bitte versuchen Sie es später erneut.',
    'Password did not conform with policy':
      'Passwort entspricht nicht den Anforderungen (mindestens 8 Zeichen)',
    'Invalid password format': 'Ungültiges Passwortformat',
    'Password reset required': 'Passwort muss zurückgesetzt werden',
    'Network error':
      'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
    NotAuthorizedException: 'Falsche E-Mail-Adresse oder Passwort',
    UserNotFoundException: 'Benutzer nicht gefunden',
    UserNotConfirmedException: 'Benutzer ist nicht bestätigt',
    CodeMismatchException: 'Ungültiger Bestätigungscode',
    InvalidPasswordException: 'Ungültiges Passwort',
    UsernameExistsException: 'Diese E-Mail-Adresse ist bereits registriert',
    LimitExceededException: 'Zu viele Versuche. Bitte warten Sie einen Moment.',
    ExpiredCodeException:
      'Der Code ist abgelaufen. Bitte fordern Sie einen neuen an.',
  }

  // Check if error code matches
  if (errorCode && errorMap[errorCode]) {
    return errorMap[errorCode]
  }

  // Check if error message contains any known error text
  const matchedKey = Object.keys(errorMap).find((key) =>
    errorMessage.includes(key)
  )

  if (matchedKey) {
    return errorMap[matchedKey]
  }

  // Default fallback
  return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'
}

const useStyles = makeStyles(() =>
  createStyles({
    main: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    },
    section: {
      maxWidth: 516,
      height: 'auto',
      minHeight: 300,
      margin: '0 auto',
      padding: '35px 40px',
      backgroundColor: '#fff',
      borderRadius: 8,
      boxShadow: '1px 1px 4px 0 rgba(0, 0, 0, 0.15)',
    },
    header: {
      fontSize: '1.15rem',
      fontWeight: 700,
      marginBottom: '0.75rem',
      color: '#152939',
      marginTop: '20px',
    },
    subtitle: {
      marginBottom: 32,
    },
    formField: {
      marginBottom: 24,
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#727279',
      marginBottom: 8,
    },
    input: {
      width: '100%',
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        '& fieldset': {
          borderColor: '#c4c4c4',
        },
        '&:hover fieldset': {
          borderColor: '#B6B6B9',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#69BE28',
        },
      },
      '& .MuiInputBase-input': {
        padding: '0px 14px',
        fontSize: '0.875rem',
        height: '50px',
        borderRadius: '3px',
      },
    },
    hint: {
      marginTop: 8,
      fontSize: '0.875rem',
      color: '#727279',
    },
    linkButton: {
      color: '#69BE28',
      textTransform: 'none',
      padding: 0,
      minWidth: 'auto',
      fontSize: '0.81rem',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      },
    },
    footer: {
      marginTop: 45,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    secondaryFooter: {
      fontSize: '0.875rem',
      color: '#727279',
    },
    submitButton: {
      minWidth: 153,
      height: 48,
      backgroundColor: '#69BE28',
      color: '#fff',
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#689f38',
        boxShadow: 'none',
      },
      '&:disabled': {
        backgroundColor: '#ccc',
      },
    },
    error: {
      color: '#DB1F1F',
      fontSize: '0.875rem',
      marginTop: 8,
    },
    success: {
      color: '#7cb342',
      fontSize: '0.875rem',
      marginTop: 8,
    },
    infoText: {
      color: '#727279',
      fontSize: '0.875rem',
      marginBottom: 16,
    },
  })
)

type ViewType =
  | 'signIn'
  | 'signUp'
  | 'confirmSignUp'
  | 'forgotPassword'
  | 'resetPassword'

const CustomLoginPage = (): JSX.Element => {
  const classes = useStyles()
  const [view, setView] = useState<ViewType>('signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSignIn = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await signIn({ username: email, password })
    } catch (err: any) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      })
      setSuccess('Bestätigungscode wurde an Ihre E-Mail gesendet')
      setView('confirmSignUp')
    } catch (err: any) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmSignUp = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      })
      setSuccess(
        'E-Mail erfolgreich bestätigt! Sie können sich jetzt anmelden.'
      )
      setCode('')
      setPassword('')
      setView('signIn')
    } catch (err: any) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await resetPassword({ username: email })
      setSuccess('Bestätigungscode wurde an Ihre E-Mail gesendet')
      setView('resetPassword')
    } catch (err: any) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password,
      })
      setSuccess('Passwort erfolgreich zurückgesetzt')
      setView('signIn')
    } catch (err: any) {
      setError(translateAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box className={classes.main}>
      <Box className={classes.section}>
        {view === 'signIn' && (
          <>
            <Typography className={classes.header}>Anmeldung</Typography>
            <Box className={classes.subtitle}>
              <LogoGrid isAuth />
            </Box>

            <form style={{ minWidth: '416px' }} onSubmit={handleSignIn}>
              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="email">
                  E-Mail-Adresse
                </label>
                <TextField
                  id="email"
                  fullWidth
                  placeholder="E-Mail-Adresse eingeben"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
              </Box>

              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="password">
                  Passwort
                </label>
                <TextField
                  id="password"
                  fullWidth
                  placeholder="Passwort eingeben"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
                <Box className={classes.hint}>
                  Passwort vergessen?{' '}
                  <Button
                    className={classes.linkButton}
                    onClick={() => setView('forgotPassword')}
                  >
                    Passwort zurücksetzen
                  </Button>
                </Box>
              </Box>

              {error && (
                <Typography className={classes.error}>{error}</Typography>
              )}
              {success && (
                <Typography className={classes.success}>{success}</Typography>
              )}

              <Box className={classes.footer}>
                <Box className={classes.secondaryFooter}>
                  Noch kein Account?{' '}
                  <Button
                    className={classes.linkButton}
                    onClick={() => setView('signUp')}
                  >
                    Neuen Account anlegen
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  className={classes.submitButton}
                >
                  {loading ? 'Anmeldung läuft...' : 'Anmelden'}
                </Button>
              </Box>
            </form>
          </>
        )}

        {view === 'signUp' && (
          <>
            <Typography className={classes.header}>Registrierung</Typography>

            <Box className={classes.subtitle}>
              <LogoGrid isAuth />
            </Box>

            <form onSubmit={handleSignUp}>
              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="signup-email">
                  E-Mail-Adresse
                </label>
                <TextField
                  id="signup-email"
                  fullWidth
                  placeholder="E-Mail-Adresse eingeben"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
              </Box>

              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="signup-password">
                  Passwort
                </label>
                <TextField
                  id="signup-password"
                  fullWidth
                  placeholder="Passwort eingeben"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
              </Box>

              {error && (
                <Typography className={classes.error}>{error}</Typography>
              )}

              <Box className={classes.footer}>
                <Box className={classes.secondaryFooter}>
                  Sie sind bereits registriert?{' '}
                  <Button
                    className={classes.linkButton}
                    onClick={() => setView('signIn')}
                  >
                    Hier Anmelden
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  className={classes.submitButton}
                >
                  {loading ? 'Registrierung läuft...' : 'Registrieren'}
                </Button>
              </Box>
            </form>
          </>
        )}

        {view === 'confirmSignUp' && (
          <>
            <Typography className={classes.header}>
              E-Mail bestätigen
            </Typography>

            <Typography className={classes.infoText}>
              Bitte geben Sie den Bestätigungscode ein, den wir an {email}{' '}
              gesendet haben.
            </Typography>

            <form onSubmit={handleConfirmSignUp}>
              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="confirm-code">
                  Bestätigungscode
                </label>
                <TextField
                  id="confirm-code"
                  fullWidth
                  placeholder="Code eingeben"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
              </Box>

              {error && (
                <Typography className={classes.error}>{error}</Typography>
              )}

              <Box className={classes.footer}>
                <Box className={classes.secondaryFooter}>
                  <Button
                    className={classes.linkButton}
                    onClick={() => setView('signIn')}
                  >
                    Zurück zur Anmeldung
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  className={classes.submitButton}
                >
                  {loading ? 'Bestätigung läuft...' : 'Bestätigen'}
                </Button>
              </Box>
            </form>
          </>
        )}

        {view === 'forgotPassword' && (
          <>
            <Typography className={classes.header}>
              Ihr Passwort zurücksetzen
            </Typography>

            <form style={{ minWidth: '416px' }} onSubmit={handleForgotPassword}>
              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="forgot-email">
                  E-Mail-Adresse
                </label>
                <TextField
                  id="forgot-email"
                  fullWidth
                  placeholder="E-Mail-Adresse eingeben"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
              </Box>

              {error && (
                <Typography className={classes.error}>{error}</Typography>
              )}

              <Box className={classes.footer}>
                <Box className={classes.secondaryFooter}>
                  <Button
                    className={classes.linkButton}
                    onClick={() => setView('signIn')}
                  >
                    Zurück zur Anmeldung
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  className={classes.submitButton}
                >
                  {loading ? 'Code wird gesendet...' : 'Zurücksetzen'}
                </Button>
              </Box>
            </form>
          </>
        )}

        {view === 'resetPassword' && (
          <>
            <Typography className={classes.header}>
              Neues Passwort eingeben
            </Typography>

            <form onSubmit={handleResetPassword}>
              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="reset-code">
                  Bestätigungscode
                </label>
                <TextField
                  id="reset-code"
                  fullWidth
                  placeholder="Code eingeben"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
              </Box>

              <Box className={classes.formField}>
                <label className={classes.label} htmlFor="new-password">
                  Neues Passwort
                </label>
                <TextField
                  id="new-password"
                  fullWidth
                  placeholder="Neues Passwort eingeben"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  variant="outlined"
                  className={classes.input}
                />
              </Box>

              {error && (
                <Typography className={classes.error}>{error}</Typography>
              )}

              <Box className={classes.footer}>
                <Box className={classes.secondaryFooter}>
                  <Button
                    className={classes.linkButton}
                    onClick={() => setView('signIn')}
                  >
                    Zurück zur Anmeldung
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  className={classes.submitButton}
                >
                  {loading ? 'Passwort wird geändert...' : 'Passwort ändern'}
                </Button>
              </Box>
            </form>
          </>
        )}
      </Box>
    </Box>
  )
}

export default CustomLoginPage
